import { useState, useEffect } from 'react';
import { Form, Row, Col, InputGroup, Card, Badge } from 'react-bootstrap';
import { FaUserTag, FaIdCard, FaCalendarAlt, FaBuilding, FaMoneyBill, FaPhone, FaUser, FaUserPlus } from 'react-icons/fa';
import { colors } from '../../../../../utils/Colors';

const EventDetailsForm = ({ formData, handleEventDetailsChange, handleCustomPerHeadChange }) => {
  
  // Function to get day name from date string
  const getDayFromDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
  };

  // Function to format date nicely
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Handle date change with auto day detection
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    
    // First update the date
    handleEventDetailsChange(e);
    
    // If date is selected, auto-detect day
    if (name === 'dateOfEvent' && value) {
      const dayName = getDayFromDate(value);
      
      // Create a synthetic event for day of event
      const dayEvent = {
        target: {
          name: 'dayOfEvent',
          value: dayName
        }
      };
      
      // Update day of event after a short delay to ensure date is updated first
      setTimeout(() => {
        handleEventDetailsChange(dayEvent);
      }, 10);
    }
  };

  // Handle other event details changes
  const handleOtherEventDetailsChange = (e) => {
    handleEventDetailsChange(e);
  };

  return (
    <Card className="mb-4 border-0 shadow-sm">
      <Card.Header className="text-white d-flex align-items-center" style={{ backgroundColor: colors.TABLE_HEADERS }}>
        <div className="p-2 me-3 bg-white bg-opacity-25 rounded-circle">
          <FaUserPlus size={20} />
        </div>
        <h5 className="mb-0">Event Details</h5>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                <FaUserTag className="me-2" />
                Customer Name *
              </Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.eventDetails.name}
                onChange={handleOtherEventDetailsChange}
                placeholder="Enter customer name"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <FaIdCard className="me-2" />
                CNIC Number
              </Form.Label>
              <Form.Control
                type="text"
                name="cnic"
                value={formData.eventDetails.cnic}
                onChange={handleOtherEventDetailsChange}
                placeholder="XXXXX-XXXXXXX-X"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <FaCalendarAlt className="me-2" />
                    Event Date *
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaCalendarAlt />
                    </InputGroup.Text>
                    <Form.Control
                      type="date"
                      name="dateOfEvent"
                      value={formData.eventDetails.dateOfEvent}
                      onChange={handleDateChange}
                      required
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </InputGroup>
                 
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Day of Event</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaCalendarAlt />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="dayOfEvent"
                      value={formData.eventDetails.dayOfEvent}
                      readOnly
                      className="bg-light fw-bold"
                      placeholder="Auto-detected"
                    />
                  </InputGroup>
                
                </Form.Group>
              </Col>
            </Row>

            {/* Quick Info Card */}
           
          </Col>

          <Col md={6}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Number of Ladies</Form.Label>
                  <Form.Control
                    type="number"
                    name="ladies"
                    value={formData.eventDetails.ladies}
                    onChange={handleOtherEventDetailsChange}
                    placeholder="0"
                    min="0"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Number of Gents</Form.Label>
                  <Form.Control
                    type="number"
                    name="gents"
                    value={formData.eventDetails.gents}
                    onChange={handleOtherEventDetailsChange}
                    placeholder="0"
                    min="0"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Total Persons *</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <FaUser />
                </InputGroup.Text>
                <Form.Control
                  type="number"
                  name="persons"
                  value={formData.eventDetails.persons}
                  onChange={handleOtherEventDetailsChange}
                  placeholder="0"
                  min="1"
                  required
                />
              </InputGroup>
             
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <FaBuilding className="me-2" />
                Venue
              </Form.Label>
              <Form.Control
                type="text"
                name="venue"
                value={formData.eventDetails.venue}
                onChange={handleOtherEventDetailsChange}
                placeholder="Enter venue name"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Lunch/Dinner</Form.Label>
              <Form.Select
                name="lunchDinner"
                value={formData.eventDetails.lunchDinner}
                onChange={handleOtherEventDetailsChange}
              >
                <option value="">Select Meal Type</option>
                <option value="Lunch">Lunch</option>
                <option value="Dinner">Dinner</option>
                <option value="Both">Both</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>
                <FaMoneyBill className="me-2" />
                Advance Payment (PKR)
              </Form.Label>
              <InputGroup>
                <InputGroup.Text>Rs</InputGroup.Text>
                <Form.Control
                  type="number"
                  name="advancePayment"
                  value={formData.eventDetails.advancePayment}
                  onChange={handleOtherEventDetailsChange}
                  placeholder="0"
                  min="0"
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>
                <FaPhone className="me-2" />
                Mobile Number 1
              </Form.Label>
              <Form.Control
                type="tel"
                name="mob1"
                value={formData.eventDetails.mob1}
                onChange={handleOtherEventDetailsChange}
                placeholder="03XX-XXXXXXX"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>
                <FaPhone className="me-2" />
                Mobile Number 2 (Optional)
              </Form.Label>
              <Form.Control
                type="tel"
                name="mob2"
                value={formData.eventDetails.mob2}
                onChange={handleOtherEventDetailsChange}
                placeholder="03XX-XXXXXXX"
              />
            </Form.Group>
          </Col>
          <Col md={8}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                <FaUser className="me-2" />
                Custom Per Head Amount (PKR) - Optional
              </Form.Label>
              <InputGroup>
                <InputGroup.Text>Rs</InputGroup.Text>
                <Form.Control
                  type="number"
                  value={formData.customPerHead}
                  onChange={handleCustomPerHeadChange}
                  placeholder="Enter custom per head amount (only for custom menu)"
                  min="0"
                />
                <InputGroup.Text>per person</InputGroup.Text>
              </InputGroup>
             
            </Form.Group>
          </Col>
        </Row>

       
      </Card.Body>
    </Card>
  );
};

export default EventDetailsForm;